import { Suspense } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import type { CalculatorEntry } from '@/types/calculator'
import { CATEGORIES, getCalcUrl } from '@/registry'
import { CalculatorCard } from '@/components/ui/CalculatorCard'
import { buildLocaleSeoLinks } from '@/i18n/seo'
import { resolveLocaleFromPath, toCurrentLocalePath } from '@/i18n/routing'
import {
  getLocalizedCalculatorMeta,
  getLocalizedCategoryLabel,
  getShellLabels,
} from '@/i18n/calculator-localization'

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://toolvance.com'

interface CalculatorShellProps {
  calc: CalculatorEntry
  allCalculators: CalculatorEntry[]
}

export function CalculatorShell({ calc, allCalculators }: CalculatorShellProps) {
  const { pathname } = useLocation()
  const { locale } = resolveLocaleFromPath(pathname)
  const localizedCalc = getLocalizedCalculatorMeta(locale, calc)
  const shellLabels = getShellLabels(locale)
  const related = calc.relatedCalculators
    .map(slug => allCalculators.find(c => c.slug === slug))
    .filter(Boolean) as CalculatorEntry[]

  const cat = CATEGORIES.find(c => c.id === calc.category)
  const categoryLabel = getLocalizedCategoryLabel(locale, calc.category, cat?.label ?? '')
  const seoLinks = buildLocaleSeoLinks(pathname || getCalcUrl(calc.slug, calc.category), SITE_URL)
  const homePath = toCurrentLocalePath(pathname, '/')
  const categoryPath = toCurrentLocalePath(pathname, `/${calc.category}`)

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: localizedCalc.name,
    description: localizedCalc.seoDescription,
    url: seoLinks.canonicalUrl,
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'Any',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  }

  return (
    <>
      <Helmet>
        <title>{localizedCalc.seoTitle}</title>
        <meta name="description" content={localizedCalc.seoDescription} />
        <link rel="canonical" href={seoLinks.canonicalUrl} />
        {seoLinks.alternates.map(alt => (
          <link key={alt.locale} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={seoLinks.xDefaultUrl} />
        <meta property="og:title" content={localizedCalc.seoTitle} />
        <meta property="og:description" content={localizedCalc.seoDescription} />
        <meta property="og:url" content={seoLinks.canonicalUrl} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
          <Link to={homePath} className="hover:text-brand-600">{shellLabels.home}</Link>
          <span>/</span>
          <Link to={categoryPath} className="hover:text-brand-600">{categoryLabel}</Link>
          <span>/</span>
          <span className="text-gray-800 font-medium">{localizedCalc.name}</span>
        </nav>

        {/* Calculator content */}
        <Suspense fallback={<div className="animate-pulse bg-gray-200 h-64 rounded-lg" />}>
          <calc.component />
        </Suspense>

        {/* Related Calculators */}
        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{shellLabels.relatedCalculators}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map(r => <CalculatorCard key={r.slug} calc={r} />)}
            </div>
          </section>
        )}
      </div>
    </>
  )
}
